import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip, useTheme } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Fab from '@material-ui/core/Fab';
import Box from '@material-ui/core/Box';
import * as MuiIcons from '@material-ui/icons';

import { formatDatetime } from '~/utils/format';
import { getApplicationIsOnline, isWeb } from '~/store/modules/app/app-selectors';
import * as pdvActions from '~/store/modules/pdv/pdv-actions';

import ModalSaldoEstoqueProduto from '~/pages/TelaVenda/modal/ModalSaldoEstoqueProduto';
import ModalSaldoEstoqueTransferencia from '~/pages/TelaVenda/modal/ModalSaldoEstoqueTransferencia';
import { Container } from './styles';
import { API_BASE_URL, API_LOCAL_URL } from '~/constants';
import api from '~/services/api';
import Skeleton from '@material-ui/lab/Skeleton';

const EstoqueProduto = React.memo(() => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { itemVenda, loadingProduto } = useSelector(state => state.pdv);
  const [showModalSaldoEstoque, setShowModalSaldoEstoque] = useState(false);
  const [showModalTransferenciaFilial, setShowModalTransferenciaFilial] = useState(false);
  const [estoques, setEstoques] = useState([]);
  const [armazens, setArmazens] = useState([]);
  const isOnline = useSelector(getApplicationIsOnline());

  const [loadingEstoque, setLoadingEstoque] = useState(false);
  
  const carregaSaldoEstoque = async () => {
    if (!itemVenda?.produtoEstProduto?.id) {
      setArmazens([]);
      setEstoques([]);
      return;
    }

    setLoadingEstoque(true);

    try {
      let data;
      const $select = ['estProdutoId', 'estArmazemId', 'estoqueReal', 'estoqueReservado', 'estoqueFuturo'];
      const $expand = [
        'estProdutoLote',
        'estProduto($select=id,codigo,descricao)',
        'estArmazem($select=id,descricao,tipo)',
        'estProdutoGrade($select=id,gradeEixoLinha,gradeEixoColuna)'
      ];
      const response = await api.get(`${API_BASE_URL}/v1/consulta/saldoestoque/?$select=${$select.join(',')} &$filter=estProdutoId eq 
      ${itemVenda?.produtoEstProduto?.id}&$expand=${$expand.join(',')}`);
      data = response.data;
      if (!isWeb && !isOnline) {
        const response = await api.get(`${API_LOCAL_URL}/estoque/produto/?queryString=${itemVenda?.produtoEstProduto?.descricao}`)
        data = response.data;
      }

      const res = data.filter(estoque => estoque?.estArmazem?.tipo !== 2);
      const armazemOnline = data.filter(armazem => armazem?.estArmazemId === itemVenda?.produtoEstArmazem?.id);

      const saldoTotal = armazemOnline.reduce(
        (acc, armazem) => {
          acc.estoqueReal += armazem.estoqueReal;
          acc.estoqueReservado += armazem.estoqueReservado;
          acc.estoqueFuturo += armazem.estoqueFuturo;
          return acc;
        },
        { estoqueReal: 0, estoqueReservado: 0, estoqueFuturo: 0 }
      );

      setArmazens([saldoTotal]);
      setEstoques([...res]);
    } catch (error) {
      console.error("Erro ao carregar saldo de estoque:", error);
      setArmazens([]);
      setEstoques([]);
    } finally {
      setLoadingEstoque(false);
    }
  };

  useEffect(() => {
    if (!itemVenda) {
      setArmazens([]);
      setEstoques([]);
      setLoadingEstoque(false);
      return;
    }

    if (itemVenda?.produtoEstProduto?.id) {
      carregaSaldoEstoque();
    }
  }, [itemVenda?.produtoEstProduto?.id, itemVenda?.produtoEstArmazemId, isOnline]);

  const getEstoqueColorDisponivel = (armazem, theme) => {
    if (armazem) {
      return armazem.estoqueReal - armazem.estoqueReservado >= 0 ? '#09A848' : 'rgb(255, 4, 4)';
    }
    return theme.palette.common.white;
  };

  const estoqueColorDisponivel = getEstoqueColorDisponivel(armazens[0], theme);

  const getEstoqueColorReal = (armazem, theme) => {
    if (armazem) {
      return armazem.estoqueReal >= 0 ? '#09A848' : 'rgb(255, 4, 4)';
    }
    return theme.palette.common.white;
  };

  const estoqueColorReal = getEstoqueColorReal(armazens[0], theme);

  const skeletonHeight = 20;
  const skeletonWidth = 40;

  return (
    <Box>
      <Typography variant="caption" gutterBottom align="left">
        ESTOQUE
      </Typography>
      <Container>
        <Grid container item justify="space-between" alignItems="center" style={{ gap: '8px' }}>
          <Grid container item md={4} alignItems="center" style={{ gap: '4px' }}>
            <MuiIcons.History />
            <Box>
              <Typography variant="caption">
                {`  Ult. Atualização ${itemVenda?.saldoEstoque ? formatDatetime(new Date()) : '-'}`}
              </Typography>
            </Box>
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid item>
            <Typography variant="caption" align="left">
              Disponível
            </Typography>
            <Typography align="left">
              {loadingEstoque ? (
                <Skeleton variant="text" width={skeletonWidth} height={skeletonHeight} style={{ margin: 'auto' }} />
              ) : (
                <b style={{ color: estoqueColorDisponivel }}>
                  {armazens[0]?.estoqueReal - armazens[0]?.estoqueReservado || 0}
                </b>
              )}
            </Typography>
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid item>
            <Typography variant="caption" align="left">
              Real
            </Typography>
            <Typography align="left">
              {loadingEstoque ? (
                <Skeleton variant="text" width={skeletonWidth} height={skeletonHeight} style={{ margin: 'auto' }}/>
              ) : (
                <b style={{ color: estoqueColorReal }}>{armazens[0]?.estoqueReal || 0}</b>
              )}
            </Typography>
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid item>
            <Typography variant="caption" align="left">
              Reservado
            </Typography>
            <Typography align="left">
              {loadingEstoque ? (
                <Skeleton variant="text" width={skeletonWidth} height={skeletonHeight} style={{ margin: 'auto' }}/>
              ) : (
                <b> {armazens[0]?.estoqueReservado || 0}</b>
              )}
            </Typography>
          </Grid>
          <Divider orientation="vertical" flexItem />
          <Grid item>
            <Typography variant="caption" align="left">
              Futuro
            </Typography>
            <Typography align="left">
              {loadingEstoque ? (
                <Skeleton variant="text" width={skeletonWidth} height={skeletonHeight} style={{ margin: 'auto' }}/>
              ) : (
                <b> {armazens[0]?.estoqueFuturo || 0}</b>
              )}
            </Typography>
          </Grid>
          {isOnline && (
            <Tooltip title="Mostrar Saldo de Estoque">
              <Fab
                size="small"
                disabled={!itemVenda?.produtoEstProduto || loadingProduto}
                aria-label="add"
                style={{
                  backgroundColor:
                    !itemVenda?.produtoEstProduto || loadingProduto
                      ? theme.palette.background.default
                      : '#FFF',
                  boxShadow: theme.shadows[6]
                }}
                onClick={() => setShowModalSaldoEstoque(true)}
              >
                <MuiIcons.OpenInNew />
              </Fab>
            </Tooltip>
          )}
          {isOnline && (
            <Tooltip title="Estoque em outras filiais">
              <Fab
                size="small"
                disabled={!itemVenda?.produtoEstProduto || loadingProduto}
                aria-label="add"
                style={{
                  backgroundColor:
                    !itemVenda?.produtoEstProduto || loadingProduto
                      ? theme.palette.background.default
                      : '#FFF',
                  boxShadow: theme.shadows[6]
                }}
                onClick={() => setShowModalTransferenciaFilial(true)}
              >
                <MuiIcons.ImportExport />
              </Fab>
            </Tooltip>
          )}
        </Grid>
      </Container>

      {showModalSaldoEstoque && (
        <ModalSaldoEstoqueProduto
          open={showModalSaldoEstoque}
          data={{
            produto: itemVenda?.produtoEstProduto,
            saldoEstoque: itemVenda?.saldoEstoque
          }}
          onClose={() => {
            setShowModalSaldoEstoque(false);
          }}
        />
      )}

      {showModalTransferenciaFilial && (
        <ModalSaldoEstoqueTransferencia
          open={showModalTransferenciaFilial}
          data={{
            produto: itemVenda?.produtoEstProduto,
            saldoEstoque: itemVenda?.saldoEstoque
          }}
          onClose={payload => {
            dispatch(
              pdvActions.setTransferenciaItem({
                intEmpresaSolicitadaId: payload?.empresa?.id,
                estArmazemSolicitadoId: payload?.saldoEstoque?.estArmazem?.id
              })
            );
            setShowModalTransferenciaFilial(false);
          }}
        />
      )}
    </Box>
  );
});

export default EstoqueProduto;
